import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, TrendUp, Cube, Coins } from "@phosphor-icons/react";
import { useValidators } from "@/hooks/useChain";
import { VALIDATORS } from "@/constants/testIds";
import { fmtAone, shortAddr } from "@/lib/api";

export default function ValidatorsPage() {
    const validators = useValidators();

    return (
        <div className="max-w-[1440px] mx-auto px-5 sm:px-6 lg:px-10 py-8 sm:py-10">
            <div className="mb-6 sm:mb-8 flex items-start justify-between flex-wrap gap-4">
                <div className="min-w-0">
                    <div className="eyebrow mb-2">Aegis Validator Set</div>
                    <h1 className="font-display text-[26px] sm:text-[32px] lg:text-[38px] font-semibold tracking-tight leading-tight">21 Genesis Validators</h1>
                    <p className="text-muted mt-2 text-[13.5px] sm:text-[14px] max-w-[640px]">
                        21 seed validators secure the Aegis One testnet across India, MENA, APAC, EU and the Americas.
                        Genesis mainnet ships with the same 21, then expands to 51 → 100+.
                    </p>
                </div>
                <Link to="/tokenomics" className="btn-ghost inline-flex items-center gap-2 shrink-0 !text-[12.5px]">
                    Tokenomics →
                </Link>
            </div>

            <div className="panel overflow-x-auto">
                <table className="tbl min-w-[900px]">
                    <thead>
                        <tr>
                            <th>Rank</th>
                            <th>Validator</th>
                            <th>Region</th>
                            <th>Address</th>
                            <th className="text-right">Total stake</th>
                            <th className="text-right">Commission</th>
                            <th className="text-right">Uptime</th>
                            <th className="text-right">Blocks</th>
                            <th className="text-right">Rewards paid</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {validators.map((v, i) => (
                            <tr key={v.id} data-testid={VALIDATORS.row(v.id)}>
                                <td className="font-mono text-slate-500">#{i + 1}</td>
                                <td>
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 flex items-center justify-center border border-white/10 rounded-sm bg-[var(--bg-surface)]">
                                            <ShieldCheck size={14} className="text-[var(--primary)]" />
                                        </div>
                                        <div>
                                            <div className="font-medium text-[14px]">{v.moniker}</div>
                                            <div className="text-[11px] text-muted max-w-[260px] truncate">{v.description}</div>
                                        </div>
                                    </div>
                                </td>
                                <td className="font-mono text-[11.5px] text-muted uppercase tracking-widest">{v.region || "-"}</td>
                                <td className="font-mono text-[11.5px] text-muted">{shortAddr(v.address, 12, 8)}</td>
                                <td className="text-right font-mono">
                                    <div className="text-[var(--primary)]">{fmtAone(v.total_stake)}</div>
                                    <div className="text-[10.5px] text-slate-500 mt-1">
                                        {fmtAone(v.self_stake)} self · {fmtAone(v.delegated_stake)} delegated
                                    </div>
                                </td>
                                <td className="text-right font-mono">{(v.commission_pct * 100).toFixed(1)}%</td>
                                <td className="text-right font-mono">
                                    <span className={v.uptime_pct >= 99 ? "text-[var(--primary)]" : "text-amber-300"}>
                                        {v.uptime_pct.toFixed(2)}%
                                    </span>
                                </td>
                                <td className="text-right font-mono">{v.blocks_proposed.toLocaleString()}</td>
                                <td className="text-right font-mono text-muted">{fmtAone(v.rewards_distributed)}</td>
                                <td className="text-right">
                                    <span className="text-[var(--text-3)] text-[11px] uppercase tracking-widest font-mono">Live</span>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
                <div className="panel p-6">
                    <TrendUp size={20} className="text-[var(--primary)] mb-3" />
                    <div className="eyebrow mb-2">Consensus</div>
                    <div className="text-[13px] text-muted leading-relaxed">
                        BFT-style consensus with ~2s finality. Proposer weighted by total stake.
                    </div>
                </div>
                <div className="panel p-6">
                    <Cube size={20} className="text-[var(--primary)] mb-3" />
                    <div className="eyebrow mb-2">Epoch length</div>
                    <div className="text-[13px] text-muted leading-relaxed">
                        Rewards distributed every 100 blocks. Testnet APY boosted for rapid feedback.
                    </div>
                </div>
                <div className="panel p-6">
                    <Coins size={20} className="text-[var(--primary)] mb-3" />
                    <div className="eyebrow mb-2">Slashing</div>
                    <div className="text-[13px] text-muted leading-relaxed">
                        Double-sign and downtime slashing designed but disabled on testnet. Enabled at mainnet.
                    </div>
                </div>
            </div>
        </div>
    );
}
